// Unified image accessor for ad galleries (#487).
//
// v2 (`websiteAdsV2`) galleries are arrays of prefetched entries
// ({ path, url, thumbUrl, blurhash, tag, type, style, index }) — the cloud
// function already resolved the download URLs, so the client never calls
// Storage. v1 (`websiteAds`) images are plain strings. These helpers let a
// component consume either shape.
//
// An entry with an empty `url` is a still-processing image on the
// immaginaCasa side: skip it, the sync re-pends it and it shows up on the
// next write of the doc.
//
// #645: which entries may be shown, and which of them may reach a card, is NOT
// decided here — it is decided once in functions/imagePolicy.js, which the SSR
// renderer requires too so the site and the crawler cannot drift apart. See
// that file's header for why it sits under functions/.

import imagePolicy from "../../functions/imagePolicy.js";

const {
  isVisibleEntry,
  cardEligibleEntries,
  roomKey,
  isFurnishedType,
  isRenovatedEmptyType,
} = imagePolicy;

export function isV2ImageEntry(image) {
  return (
    !!image &&
    typeof image === "object" &&
    ("url" in image || "thumbUrl" in image)
  );
}

export function displayableEntries(entries) {
  if (!Array.isArray(entries)) return [];
  return entries.filter((e) => isV2ImageEntry(e) && isVisibleEntry(e));
}

// The project-gallery entries a card (and the JSON-LD gallery) may show. Thin
// wrapper so every site-side consumer reaches the shared policy through this
// module rather than importing across the functions/ boundary itself.
export function cardEntries(entries) {
  if (!Array.isArray(entries)) return [];
  return cardEligibleEntries(entries.filter(isV2ImageEntry));
}

// The listing-card slider used to mix every AI furnishing style together
// (rome + paris + … all in one sequence), so the arrows stepped through
// near-duplicate renders of the same rooms instead of touring the apartment.
//
// A room's `furnish` render is NOT style-less in the raw data: websiteSync
// writes one `furnish` PER STYLE PASS (e.g. `furnish`/style:"rome" alongside
// `furnish`/style:"paris"), each one a plain/base restaging that pass
// produced on the way to its `furnishStyle` — visually undecorated, unlike
// the `furnishStyle` render of the SAME pass. So gating on `e.style` alone
// (as an earlier version of this function did) let a room's bare `furnish`
// through every time its style tag happened to match the primary style,
// pairing it right next to the actual styled render. Only `furnishStyle` /
// `nakedRenewedStyle` are the DEFINITIVELY styled types; every other type
// (`furnish`, `nakedRenewed`, a legacy untyped photo) is a fallback
// regardless of which style pass' tag it happens to carry.
//
// This isolates ONE style, room by room:
//   - a definitively-styled entry (furnishStyle/nakedRenewedStyle) survives
//     only when it's the primary style's own;
//   - a fallback entry (furnish/nakedRenewed/legacy) survives only for a room
//     the primary style has NO definitively-styled render for, so a covered
//     room never shows both its bare and its styled render, and an
//     uncovered room isn't dropped from the tour entirely.
// The result stays room-ordered (source order), so the arrows step room by
// room within that one style.
//
// The style shown is picked by pickPrimaryStyle() (below): one of the styles
// present, chosen per ad from `seed` so the list doesn't stage every flat in
// the same style.
export function primaryStyleCardEntries(entries, { seed } = {}) {
  const base = cardEntries(entries);
  const isStyled = (e) => e.style && (e.type === "furnishStyle" || e.type === "nakedRenewedStyle");
  const styled = base.filter(isStyled);
  if (styled.length === 0) return base; // no per-style renders to isolate

  const seen = [];
  styled.forEach((e) => {
    if (!seen.includes(e.style)) seen.push(e.style);
  });
  const primary = pickPrimaryStyle(seen, seed);

  // Rooms (tags) the primary style genuinely covers — every fallback entry of
  // one of these rooms is redundant. Untagged entries (tag null/empty) share
  // no identity with one another — same convention as roomKey() — so a null
  // "room" is never considered covered.
  const primaryTags = new Set();
  base.forEach((e) => {
    if (isStyled(e) && e.style === primary && e.tag) primaryTags.add(e.tag);
  });

  return base.filter((e) => {
    if (isStyled(e)) return e.style === primary;
    return !e.tag || !primaryTags.has(e.tag);
  });
}

// v2 entries carry a responsive WebP ladder written by the cloud function:
//   variants: [{ w, url }]  — ascending real widths (400/800/1600/2560…)
// Older/not-yet-resynced entries (and v1) have no `variants`; every accessor
// below degrades to thumbUrl/url so those keep rendering unchanged.
function entryVariants(entry) {
  return isV2ImageEntry(entry) && Array.isArray(entry.variants) ? entry.variants : [];
}

// Default <img sizes> for the listing-card image: full width below the desktop
// breakpoint, ~half the row on desktop. Tune to the card layout if it changes.
export const CARD_IMAGE_SIZES = "(max-width: 1023px) 100vw, 560px";

// Thumb (smallest variant) for cards/grids as the plain `src`; the srcset/
// image-set helpers below add the responsive upgrade. Original as last resort.
export function entryDisplayUrl(entry) {
  if (!isV2ImageEntry(entry)) return "";
  return entry.thumbUrl || entry.url || "";
}

// Bounded large variant for galleries/lightboxes/detail backgrounds — the site
// never downloads the raw original (which can be several MB). Falls back to the
// original, then the thumb, for entries without a ladder.
export function entryFullUrl(entry) {
  if (!isV2ImageEntry(entry)) return "";
  const v = entryVariants(entry);
  const largest = v.length ? v[v.length - 1] : null;
  return (largest && largest.url) || entry.url || entry.thumbUrl || "";
}

// Variante "giusta" per una larghezza in pixel fisici: la più piccola con
// `w` ≥ targetWidth, altrimenti la più grande. Serve al dettaglio annuncio su
// telefono: la variante massima (2048–2560px) decodificata pesa ~16 MB, dieci
// volte i pixel di uno schermo da 375px, e su un annuncio con molte stanze
// faceva uccidere la pagina a iOS per memoria. Senza un target valido, o per
// voci senza ladder, il comportamento è esattamente quello di entryFullUrl.
//
// `avif: true` (solo se il browser lo decodifica): se la variante scelta ha
// anche la copia AVIF (`{ w, url, avif }`, scritta dalla cloud function per le
// foto del dettaglio) usa quella. Il WebP lossy sulle superfici lisce (pareti,
// soffitti) fa chiazze di colore che si vedono anche a qualità alta; l'AVIF a
// peso simile no. Le card restano sul WebP.
export function entryUrlForWidth(entry, targetWidth, { avif = false } = {}) {
  if (!(targetWidth > 0)) return entryFullUrl(entry);
  const v = entryVariants(entry)
    .filter((x) => x && x.url && x.w)
    .sort((a, b) => a.w - b.w);
  if (!v.length) return entryFullUrl(entry);
  const fit = v.find((x) => x.w >= targetWidth) || v[v.length - 1];
  return (avif && fit.avif) || fit.url;
}

// Larghezza in pixel fisici che serve alle foto del dettaglio annuncio. Lo
// slider le mostra in `background-size: cover`: sotto i 1024px il riquadro è
// largo quanto lo schermo ma alto 350px fissi, quindi una foto 3:2 viene
// ingrandita fino a 525px CSS di larghezza (più dello schermo di un telefono)
// e il resto tagliato ai lati. Contare solo la larghezza dello schermo faceva
// scegliere la 800w su un telefono a DPR 2–3, poi ingrandita del 30–100%:
// foto sgranate. Da desktop il riquadro è alto al più min(87vh, 800px).
// Il DPR resta limitato a 2: oltre la differenza non si vede ma la memoria sì
// (bug iOS sulle varianti grandi). Senza viewport → undefined (variante più
// grande, come entryFullUrl).
const DETAIL_PHOTO_ASPECT = 1.5;
export function detailPhotoTargetWidth({ width, height, dpr } = {}) {
  if (!(width > 0)) return undefined;
  const boxHeight = width < 1024 ? 350 : Math.min((height > 0 ? height : 0) * 0.87, 800);
  const cssWidth = Math.max(width, boxHeight * DETAIL_PHOTO_ASPECT);
  return Math.ceil(cssWidth * Math.min(dpr || 1, 2));
}

// Responsive srcset ("url w, …") from the WebP ladder for <img srcset>. "" when
// there are no variants, so the caller's plain `src` still applies.
export function entrySrcSet(entry) {
  return entryVariants(entry)
    .filter((v) => v && v.url && v.w)
    .map((v) => `${v.url} ${v.w}w`)
    .join(", ");
}

// CSS image-set() for FIXED-size backgrounds (the compra card): the DPR picks
// 1x/2x from the two smallest variants. "" (→ caller keeps its plain url())
// unless there are at least two variants. Unprefixed image-set is supported by
// every current browser; older ones fall back to the url() the caller sets.
export function entryImageSet(entry) {
  const v = entryVariants(entry).filter((x) => x && x.url && x.w);
  if (v.length < 2) return "";
  return `image-set(url("${v[0].url}") 1x, url("${v[1].url}") 2x)`;
}

// Map a v2 gallery to the index-aligned { images, blurhashes } pair the
// existing components (ListingCard + BlurhashImage) already consume.
export function galleryToLegacy(entries) {
  const usable = displayableEntries(entries);
  return {
    images: usable.map(entryDisplayUrl),
    blurhashes: usable.map((e) => e.blurhash || ""),
    srcsets: usable.map(entrySrcSet), // responsive srcset for <img> cards
    imagesets: usable.map(entryImageSet), // image-set() for background cards
  };
}

// Adapt a single image (v2 entry or v1 string) to BlurhashImage props.
// v1 strings carry no hash of their own, so the caller passes the one from
// the index-aligned imageBlurhashes array.
export function toImageSource(image, fallbackHash = "") {
  if (isV2ImageEntry(image)) {
    return { src: entryDisplayUrl(image), hash: image.blurhash || "" };
  }
  if (typeof image === "string") {
    return { src: image, hash: fallbackHash };
  }
  return { src: "", hash: "" };
}

// Agency logos live at `agencies/{agencyId}/agency-profile_thumbnail.png`
// (publicly readable). The immaginaCasa live-sync does not (yet) copy the logo
// into the ad's embedded `agency.logo`, so build the URL from the agencyId as a
// fallback. The bucket is read from one of the ad's own prefetched image URLs
// so it always matches the ad's environment (no firebase dependency here).
// A missing logo 404s and the background-image simply doesn't render — same as
// an empty logo, never a broken-image icon.
export function agencyLogoUrl(agencyId, sampleImageUrl) {
  if (!agencyId || !sampleImageUrl) return "";
  const bucket = /\/b\/([^/]+)\/o\//.exec(sampleImageUrl);
  if (!bucket) return "";
  const path = encodeURIComponent(`agencies/${agencyId}/agency-profile_thumbnail.png`);
  return `https://firebasestorage.googleapis.com/v0/b/${bucket[1]}/o/${path}?alt=media`;
}

// ── immaginaCasa v2 style selector ───────────────────────────────────────────
// Fixed display order + labels for the AI furnishing styles. Unknown styles
// are appended in first-seen order and titled by their raw key.
export const STYLE_ORDER = ["rome", "paris", "madrid", "new-york"];

// The ONE furnishing style a listing card shows, among the styles `seen` in the
// ad. Without a seed: the first in STYLE_ORDER (then first-seen for a style
// outside that list). With a seed (the ad's id): one of them picked by a hash
// of the seed. Always taking the first made every card in the list reveal
// `rome` under the lens, so the flats all looked alike; the hash spreads the
// styles across the list while keeping each ad's pick stable across reloads
// and re-renders (no card flipping style under the user's pointer).
export function pickPrimaryStyle(seen, seed) {
  if (!seen || seen.length === 0) return null;
  const ordered = [
    ...STYLE_ORDER.filter((s) => seen.includes(s)),
    ...seen.filter((s) => !STYLE_ORDER.includes(s)),
  ];
  if (!seed) return ordered[0];
  // FNV-1a, 32 bit: tiny, deterministic, well spread on short ids.
  let h = 0x811c9dc5;
  const s = String(seed);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return ordered[(h >>> 0) % ordered.length];
}

export const STYLE_LABELS = {
  rome: "Roma",
  paris: "Parigi",
  madrid: "Madrid",
  "new-york": "New York",
};

// The night version of a `furnishStyle` render (same tag / occurrence / style).
// Not a furnished type for functions/imagePolicy.js on purpose: it never
// drives a slide, never reaches a card and never creates a style (#650).
export const FURNISHED_NIGHT_TYPE = "furnishStyleNight";

// Group a v2 project gallery into per-style, room-aligned render galleries for
// the detail-page style selector.
//
// The FURNISHED galleries ('furnish' as the base, 'furnishStyle' per named AI
// style) drive the normal view. Slides follow the photographs in source order,
// so the Immagina/Attuale before-after stays aligned per slide; slides for
// renders that match no photograph are appended after them (see
// assignEntrySlots above). Nothing is re-sorted: the photographs keep the
// order the agent set in «Annuncio e file», cover first (see "Slide order" in
// assignEntrySlots).
//
// The "senza arredo" galleries are per style too (#645). `nakedRenewedStyle` is
// the final visible renovation result; `nakedRenewed` is the pipeline's
// INTERNAL NEUTRAL one and is resolved only where no usable styled render
// exists for that SLIDE — never alongside it. `nakedInteriors` (the emptied
// real photo) stays style-less, on the Attuale side.
//
// A STYLE NEVER FALLS BACK TO ANOTHER STYLE. Per slide:
//   furnished:  selected furnishStyle → same-slide base `furnish` → the
//               slide's genuine photograph → '' (unavailable)
//   renovation: selected nakedRenewedStyle → same-slide neutral
//               `nakedRenewed` → '' (the caller then tries the photograph)
// Every array is slide-aligned and the same length as `rooms`.
//
// `hasFurnished` reports whether the ad actually carries furnished renders
// (the agent ticked the "arredo" step at creation). Because `base`/`styles`
// fall back to the real photo for rooms without a render, their length alone
// can't tell "no arredo" from "arredo present" — this flag can.
//
// Every array below is SLIDE-aligned and the same length: see assignEntrySlots
// above for how entries are assigned to slides.
//
// Returns {
//   rooms:[tag|null], actuals:[url|''], hasActual:[bool], base:[url],
//   baseFurnished:[bool],
//   styles:[{ key, title, images:[url], covered:[bool], furnished:[bool],
//             nights:[url|''], emptyImages:[url], emptyCovered:[bool] }],
//   emptyRenders:[url], emptyActuals:[url],
//   hasFurnished:bool, hasRenovation:bool, hasActualPhotos:bool
// }.
//
// `styles[k].nights[i]` is the NIGHT version of `styles[k].images[i]` — the
// style's own `furnishStyleNight` on the same slot — or '' when the style has
// no night there OR no day render of its own there (`covered[i]` false): the
// day/night toggle must never pair a night image with the base furnish or the
// photograph the day gallery fell back to. Night entries take part in no day
// bucket, create no slide and no style (#650). On a project-only slot (no
// photograph) a `furnishStyleNight` entry that precedes its own `furnishStyle`
// in source order is dropped; websiteSync writes history in chain order, so
// this does not occur in practice.
//
// `actuals[i]` is a genuine photograph or '' — never a render. A slide that
// exists only because a project render carries its tag has no "stato attuale",
// and `hasActual[i]` is how a caller tells the two apart.
//
// `styles[k].furnished[i]` / `baseFurnished[i]` say whether `images[i]` /
// `base[i]` is a furnished render (styled or base `furnish`) rather than the
// photograph fallback (#654). A slide with a renovation render but no furnished
// one is shown by the page as a renovation slide, with no furniture switch.
// ── Slot identity and occurrence matching (#645) ───────────────────────────
// Assign every displayable project entry to a slide SLOT and return the slots
// in display order. Shared by buildStyleGalleries (the detail page's per-style
// galleries) and cardPhotoRenderPairs (the listing card's photo↔render lens):
// the rules below were learned one production bug at a time, so a second
// implementation of them would be a second copy of the same bugs.
//
// Returns { slots, entrySlot, projectDisplayable, actualEntries } — ENTRIES,
// not urls: each caller decides which size of each entry it wants.
//   slots[i]             = { tag, actual: photo entry|null, owner: entry|null }
//   entrySlot[k]         = slot index of projectDisplayable[k], or -1
//   projectDisplayable   = the displayable project entries, source order
//   actualEntries        = the displayable photographs, source order
//
// A slide is a SLOT, not a "room name".
//
//  * TAGGED entry  → slot (tag, occurrence). The occurrence counts entries
//    with that tag WITHIN THEIR BUCKET (type + style), so the 2nd `rome`
//    render of "Camera 1" lines up with the 2nd photograph of "Camera 1" —
//    and with the 2nd `paris` render, keeping the styles aligned with each
//    other. One render is therefore never reused across several photographs.
//  * UNTAGGED entry (null / undefined / "" / non-string) → its OWN slot,
//    always. It has no identity to share, so it is never grouped with another
//    untagged entry, never matched to an untagged photograph, and can never
//    suppress anything.
//
// Photographs claim their slots first, in source order. A project entry that
// matches no photograph slot gets a project-only slide appended after them,
// so a usable render is never dropped just because its photograph is missing
// or still processing — EXCEPT when every photograph of that room is already
// on screen (#660): there the extra entry is a re-run of a step (a
// correction, a regeneration), not another room, and a slide of its own would
// show a render with "stato attuale" missing. See the census below.
//
// Maps are keyed by slot INDEX, never by the raw tag: object keys coerce
// null/undefined to "null"/"undefined" (which merged unrelated entries) and a
// tag called "__proto__" reads as present through the prototype chain while
// storing nothing (which lost the image and lied to `covered`).
function assignEntrySlots(projectEntries, currentEntries) {
  const actualEntries = displayableEntries(currentEntries);
  const projectDisplayable = displayableEntries(projectEntries);

  const slots = [];
  const slotIndexById = new Map();

  // #660: how many photographs EXIST for a tag, counting the ones still
  // processing (empty `url`) that `displayableEntries` drops. They are the
  // reason a render may legitimately find no photograph slot yet — its
  // photograph is on its way — and they are what tells that case apart from a
  // render that is merely a RE-RUN of a step already on screen. Entries hidden
  // on purpose (`hide_it`) are not counted: they will never claim a slot.
  const photoCensus = new Map();
  (Array.isArray(currentEntries) ? currentEntries : []).forEach((e) => {
    if (!isV2ImageEntry(e) || e.hide_it === true) return;
    const tag = roomKey(e);
    if (tag === null) return;
    photoCensus.set(tag, (photoCensus.get(tag) || 0) + 1);
  });

  const photoOccurrences = new Map();
  actualEntries.forEach((e) => {
    const tag = roomKey(e);
    let id = null;
    if (tag !== null) {
      const n = photoOccurrences.get(tag) || 0;
      photoOccurrences.set(tag, n + 1);
      id = tag + "#" + n;
    }
    slots.push({ tag: e.tag, actual: e, owner: null });
    if (id !== null) slotIndexById.set(id, slots.length - 1);
  });

  // Bucket = one gallery a slot can hold an entry in: the type, plus the style
  // for the two per-style types. Occurrences are counted per bucket.
  const bucketOf = (e) => `${e.type || ""}|${e.style || ""}`;
  const bucketOccurrences = new Map();
  const slotIdOf = (e) => {
    const tag = roomKey(e);
    if (tag === null) return null;
    const bucket = bucketOf(e);
    if (!bucketOccurrences.has(bucket)) bucketOccurrences.set(bucket, new Map());
    const perTag = bucketOccurrences.get(bucket);
    const n = perTag.get(tag) || 0;
    perTag.set(tag, n + 1);
    return tag + "#" + n;
  };

  // Only a render that can drive a slide may create one. `nakedInteriors` is an
  // overlay on the Attuale layer, and untyped/unknown entries are not gallery
  // sources — neither invents a slide, so migrated newarcHome ads (every
  // project entry untyped) keep exactly the galleries they have today.
  const drivesSlide = (e) => isFurnishedType(e) || isRenovatedEmptyType(e);

  // entry index → slot index (-1 when the entry belongs to no slide).
  const entrySlot = projectDisplayable.map((e) => {
    const id = slotIdOf(e);
    if (id !== null && slotIndexById.has(id)) return slotIndexById.get(id);
    if (!drivesSlide(e)) return -1;
    // #660: this tagged entry claims an occurrence no photograph has. Either a
    // photograph for it is still on its way — keep the project-only slide, the
    // #645 case — or every photograph of that room is already on screen, and
    // this is a re-run of a step (a correction, a regeneration) that would
    // otherwise get a slide of its own with nothing to show on the Attuale
    // side. Rooms with NO photograph at all (render-only ads, "in costruzione")
    // count zero here and keep their slides.
    if (id !== null) {
      const tag = roomKey(e);
      const known = photoCensus.get(tag) || 0;
      if (known > 0 && known <= (photoOccurrences.get(tag) || 0)) return -1;
    }
    slots.push({ tag: e.tag, actual: null, owner: e });
    const idx = slots.length - 1;
    if (id !== null) slotIndexById.set(id, idx);
    return idx;
  });

  // ── Slide order: exactly the agent's order ───────────────────────────────
  // The photographs keep the order of the ad's images, the one the agent sets
  // in the platform's «Annuncio e file» page (the first is the cover). Nothing
  // is re-sorted here: a room the AI produced nothing for stays where the
  // agent put it. Slides with no photograph behind them (render-only rooms)
  // follow, in source order.

  return { slots, entrySlot, projectDisplayable, actualEntries };
}

// `targetWidth` (facoltativo, pixel fisici): chi conosce lo schermo — il
// dettaglio annuncio — chiede la variante adatta invece della più grande; senza,
// ogni galleria resta sulla variante massima come prima. `avif`: vedi
// entryUrlForWidth.
export function buildStyleGalleries(projectEntries, currentEntries, { targetWidth, avif = false } = {}) {
  const { slots, entrySlot, projectDisplayable, actualEntries } =
    assignEntrySlots(projectEntries, currentEntries);
  const imageUrl = (e) => entryUrlForWidth(e, targetWidth, { avif });

  const rooms = slots.map((slot) => slot.tag);

  // Genuine photograph per slide — NEVER a render. A project-only slide has no
  // "stato attuale" at all, and saying otherwise would pass a render off as an
  // original photograph and invite a before/after that compares one with itself.
  const actuals = slots.map((slot) => (slot.actual ? imageUrl(slot.actual) : ""));
  const hasActual = slots.map((slot) => !!slot.actual);

  const bySlot = (predicate) => {
    const map = new Map();
    projectDisplayable.forEach((e, k) => {
      const i = entrySlot[k];
      if (i < 0 || !predicate(e)) return;
      if (!map.has(i)) map.set(i, e);
    });
    return map;
  };

  const stylesSeen = (predicate) => {
    const seen = [];
    projectDisplayable.forEach((e, k) => {
      if (entrySlot[k] < 0 || !predicate(e) || !e.style) return;
      if (!seen.includes(e.style)) seen.push(e.style);
    });
    return seen;
  };

  const isFurnishedEntry = (e) => isFurnishedType(e);
  const furnished = projectDisplayable.filter(isFurnishedEntry);

  const baseBySlot = bySlot((e) => e.type === "furnish");
  const furnishStyleSeen = stylesSeen((e) => e.type === "furnishStyle");
  const emptyStyleSeen = stylesSeen((e) => e.type === "nakedRenewedStyle");

  // The NEUTRAL renovation render per slide: the per-style fallback only. A
  // style with no render of its own for a slide shows that slide's neutral
  // image, never another style's or another slide's.
  const neutralEmptyBySlot = bySlot((e) => e.type === "nakedRenewed");
  // Any styled renovation render per slide, whatever its style — used only to
  // keep the style-less `emptyRenders` array off the neutral image.
  const anyStyledEmptyBySlot = bySlot((e) => e.type === "nakedRenewedStyle");
  const emptyActualBySlot = bySlot((e) => e.type === "nakedInteriors");

  // A style is offered when EITHER output carries it: an ad whose furnishing
  // step produced nothing for a style must not lose that style's bare renders.
  const allSeen = [
    ...furnishStyleSeen,
    ...emptyStyleSeen.filter((s) => !furnishStyleSeen.includes(s)),
  ];

  const ordered = [
    ...STYLE_ORDER.filter((s) => allSeen.includes(s)),
    ...allSeen.filter((s) => !STYLE_ORDER.includes(s)),
  ];

  // Furnished gallery, per slide:
  //   selected furnishStyle → the SAME SLOT's base `furnish` → the slot's
  //   genuine photograph → '' (unavailable).
  // Never another style's render: a slide brought into existence by style Y
  // must not display Y's image while X is selected, so the chain terminates at
  // the photograph, and a project-only slide (which has none) at ''.
  const renderFor = (i, styledBySlot) => {
    const e = (styledBySlot && styledBySlot.get(i)) || baseBySlot.get(i);
    return e ? imageUrl(e) : actuals[i];
  };

  const base = slots.map((slot, i) => renderFor(i, null));

  // `furnished[i]` (per style) and `baseFurnished[i]`: is the image the
  // furnished gallery holds for slide i a real furnished render, or the
  // photograph it fell back to? The page needs the distinction (#654): on a
  // slide that has a renovation render but no furnished one, the furnished
  // gallery would otherwise put the ORIGINAL PHOTO on the Immagina side and
  // still offer an arredato/senza-arredo switch whose "con arredo" is that
  // photo. Such a slide is handled like a renovation-only ad instead.
  const furnishedFor = (i, styledBySlot) =>
    !!((styledBySlot && styledBySlot.get(i)) || baseBySlot.get(i));
  const baseFurnished = slots.map((slot, i) => furnishedFor(i, null));

  // Renovation gallery, per slide:
  //   selected style's `nakedRenewedStyle` → the SAME SLOT's neutral
  //   `nakedRenewed` → '' (the caller then tries the slot's genuine
  //   photograph, and finally shows its unavailable state).
  // The owning render of a project-only slide is reachable ONLY through its own
  // style — pinning it here would show style Y under style X.
  const emptyImageFor = (i, styledEmptyBySlot) => {
    const e = styledEmptyBySlot.get(i) || neutralEmptyBySlot.get(i);
    return e ? imageUrl(e) : "";
  };

  // `covered[i]` = the style really has a render for slide i (vs. the base
  // furnish / photograph the gallery falls back to). The detail-page picker
  // uses it to offer, image by image, only the styles that exist for it.
  // `emptyCovered[i]` is the same for the renovation view, and is never true
  // without a real image of that style behind it.
  const styles = ordered.map((key) => {
    const styledBySlot = bySlot((e) => e.type === "furnishStyle" && e.style === key);
    const styledEmptyBySlot = bySlot(
      (e) => e.type === "nakedRenewedStyle" && e.style === key
    );
    // The style's night renders, resolved through the same slot identity
    // (tag + occurrence within the `furnishStyleNight|<style>` bucket), so the
    // n-th night of a room lines up with the n-th day render of that room.
    // A night is published only where the style's OWN day render is on that
    // slide — see the header note (#650).
    const nightBySlot = bySlot((e) => e.type === FURNISHED_NIGHT_TYPE && e.style === key);
    return {
      key,
      title: STYLE_LABELS[key] || key,
      images: slots.map((slot, i) => renderFor(i, styledBySlot)),
      covered: slots.map((slot, i) => !!styledBySlot.get(i)),
      furnished: slots.map((slot, i) => furnishedFor(i, styledBySlot)),
      nights: slots.map((slot, i) => {
        const night = styledBySlot.get(i) ? nightBySlot.get(i) : null;
        return night ? imageUrl(night) : "";
      }),
      emptyImages: slots.map((slot, i) => emptyImageFor(i, styledEmptyBySlot)),
      emptyCovered: slots.map((slot, i) => !!styledEmptyBySlot.get(i)),
    };
  });

  // Style-less "senza arredo" list, used by ads that expose no styles at all.
  // `nakedRenewed` is an internal neutral result, so it appears here only where
  // the slide has no styled render — otherwise this array would be the one
  // place left that still publishes the neutral image over a real one.
  const emptyRenders = slots.map((slot, i) => {
    const e = anyStyledEmptyBySlot.get(i) || neutralEmptyBySlot.get(i);
    return e ? imageUrl(e) : "";
  });
  const emptyActuals = slots.map((slot, i) => {
    const e = emptyActualBySlot.get(i);
    return e ? imageUrl(e) : "";
  });

  const hasRenovation =
    emptyRenders.some(Boolean) ||
    styles.some((s) => s.emptyImages.some(Boolean));

  return {
    rooms,
    actuals,
    base,
    baseFurnished,
    styles,
    emptyRenders,
    emptyActuals,
    hasFurnished: furnished.length > 0,
    hasRenovation,
    // Does the ad have a real "stato attuale" photograph at all? Rooms
    // appended from the project gallery borrow a render as their `actuals`
    // stand-in so no slide is blank — which means `actuals.length` can no
    // longer answer this. The Attuale/Immagina switch needs the real answer,
    // or an ad with only renders would offer to compare a render with itself.
    hasActualPhotos: actualEntries.length > 0,
    // Per-slide genuine-photograph coverage. `hasActualPhotos` alone cannot say
    // whether THIS slide has an original to compare against.
    hasActual,
  };
}

// The listing card's photo↔render pairs: the photographs of the current state,
// each with the furnished render of the SAME framing behind it.
//
// Why (#annunci-media-stile-filter, then this): once the card slider was
// narrowed to a single furnishing style, every ad in the list was staged in the
// same style (`rome`, the first of STYLE_ORDER) — one room per type, all of
// them looking alike, and the eye could no longer tell one property from
// another. The card now shows the REAL photographs, which are as different from
// each other as the flats are; the AI render is still one gesture away, revealed
// under a circular lens that follows the pointer over the photo.
//
// Pairing runs on SLOTS (assignEntrySlots), never on room names: the 2nd photo
// of "Camera 1" is paired with the 2nd `rome` render of "Camera 1", an untagged
// entry is an island of its own, and a tag such as "__proto__" is an opaque
// identity, not an object key.
//
// Per slot the render is: the primary style's `furnishStyle` → the SAME slot's
// base `furnish`. Never another style's render (the lens would contradict the
// rest of the card), never another slot's (the lens would reveal a different
// room). `furnishStyleNight`, the renovation types (`nakedRenewed*`),
// `nakedInteriors` and unknown future types are not furnished renders and are
// never revealed here.
//
// The photograph is the slot's genuine `actual`: a project-only slot (a render
// whose photograph is missing or still processing) yields no pair — a lens has
// nothing to reveal without a photo underneath it. An ad with no pair at all
// (no photos, no furnishing, a migrated v1/newarcHome ad) returns [] and the
// caller keeps its current behaviour.
//
// The primary style is chosen exactly as in primaryStyleCardEntries(), by
// pickPrimaryStyle() among the `furnishStyle` renders that belong to a slot,
// seeded by the ad's id when the caller passes one.
//
// Returns [{ photo, render }] — ENTRIES, in slot order.
export function cardPhotoRenderPairs(projectEntries, currentEntries, { seed } = {}) {
  const { slots, entrySlot, projectDisplayable } = assignEntrySlots(
    projectEntries,
    currentEntries
  );

  const onSlot = (k) => entrySlot[k] >= 0;
  const isStyled = (e) => e.type === "furnishStyle" && !!e.style;

  const seen = [];
  projectDisplayable.forEach((e, k) => {
    if (!onSlot(k) || !isStyled(e)) return;
    if (!seen.includes(e.style)) seen.push(e.style);
  });
  const primary = pickPrimaryStyle(seen, seed);

  // First entry of each kind per slot — source order, so a re-run that lands on
  // the same slot never displaces the render already chosen for it.
  const firstBySlot = (predicate) => {
    const map = new Map();
    projectDisplayable.forEach((e, k) => {
      const i = entrySlot[k];
      if (i < 0 || !predicate(e)) return;
      if (!map.has(i)) map.set(i, e);
    });
    return map;
  };

  const styledBySlot = firstBySlot(
    (e) => primary !== null && isStyled(e) && e.style === primary
  );
  // The bare `furnish` fallback, preferring the primary style's own pass:
  // websiteSync writes one `furnish` PER STYLE PASS (see primaryStyleCardEntries
  // above), so a slot can hold `furnish`/rome and `furnish`/paris at once. They
  // are near-identical undecorated restagings, but when the lens falls back to
  // one it may as well be the pass the rest of the card belongs to.
  const primaryBaseBySlot = firstBySlot(
    (e) => e.type === "furnish" && primary !== null && e.style === primary
  );
  const baseBySlot = firstBySlot((e) => e.type === "furnish");

  const pairs = [];
  slots.forEach((slot, i) => {
    if (!slot.actual) return;
    const render =
      styledBySlot.get(i) || primaryBaseBySlot.get(i) || baseBySlot.get(i);
    if (!render) return;
    pairs.push({ photo: slot.actual, render });
  });
  return pairs;
}
